if (!customElements.get('media-gallery')) {
  customElements.define(
    'media-gallery',
    class MediaGallery extends HTMLElement {
      constructor() {
        super();
        this.elements = {
          liveRegion: this.querySelector('[id^="GalleryStatus"]'),
          viewer: this.querySelector('[id^="GalleryViewer"]'),
          thumbnails: this.querySelector('[id^="GalleryThumbnails"]'),
        };
        this.mql = window.matchMedia('(min-width: 750px)');
        this.initGalleryNav();
        this.initGalleryDots();

        if (this.elements.viewer) {
          this.elements.viewer.addEventListener('slideChanged', (event) => {
            if (event.detail?.currentPage) this.syncGalleryDots(event.detail.currentPage);
          });
          this.elements.viewer.addEventListener('slideChanged', debounce(this.onSlideChanged.bind(this), 500));
        }

        if (!this.elements.thumbnails) return;

        this.elements.thumbnails.querySelectorAll('[data-target]').forEach((mediaToSwitch) => {
          mediaToSwitch
            .querySelector('button')
            .addEventListener('click', this.setActiveMedia.bind(this, mediaToSwitch.dataset.target, false));
        });
        if (this.dataset.desktopLayout.includes('thumbnail') && this.mql.matches) this.removeListSemantic();
      }

      initGalleryNav() {
        if (!this.elements.viewer) return;

        this.prevButton = this.querySelector('[data-gallery-prev]');
        this.nextButton = this.querySelector('[data-gallery-next]');

        if (this.prevButton) {
          this.prevButton.addEventListener('click', (event) => {
            event.preventDefault();
            this.cycleMedia(-1);
          });
        }

        if (this.nextButton) {
          this.nextButton.addEventListener('click', (event) => {
            event.preventDefault();
            this.cycleMedia(1);
          });
        }

        this.initPointerSwipe(this.elements.viewer);
      }

      initGalleryDots() {
        this.galleryDots = Array.from(this.querySelectorAll('[data-gallery-dots] [data-slide-index]'));
        if (!this.galleryDots.length) return;

        this.galleryDots.forEach((dot) => {
          dot.addEventListener('click', (event) => {
            event.preventDefault();
            const index = parseInt(dot.getAttribute('data-slide-index'), 10);
            const slides = this.getSlides();
            if (Number.isNaN(index) || !slides[index]) return;
            this.setActiveMedia(slides[index].dataset.mediaId, false);
            this.syncGalleryDots(index + 1);
          });
        });
      }

      syncGalleryDots(page) {
        if (!this.galleryDots?.length) return;
        this.galleryDots.forEach((dot, i) => {
          const on = i === page - 1;
          dot.classList.toggle('slider-counter__link--active', on);
          dot.setAttribute('aria-selected', on ? 'true' : 'false');
        });
      }

      getSlides() {
        // Only top-level gallery slides — nested modal/deferred nodes also have data-media-id.
        return Array.from(this.elements.viewer.querySelectorAll('.product__media-item[data-media-id]'));
      }

      cycleMedia(direction) {
        const slides = this.getSlides();
        if (slides.length < 2) return;

        const activeIndex = Math.max(
          0,
          slides.findIndex((slide) => slide.classList.contains('is-active'))
        );
        let nextIndex = activeIndex + direction;
        if (nextIndex < 0) nextIndex = slides.length - 1;
        if (nextIndex >= slides.length) nextIndex = 0;

        this.setActiveMedia(slides[nextIndex].dataset.mediaId, false);
      }

      initPointerSwipe(target) {
        let startX = 0;
        let startY = 0;
        let tracking = false;
        let pointerId = null;

        const isInteractiveTarget = (node) => {
          return Boolean(
            node.closest(
              'button, a, input, textarea, select, summary, .product__media-toggle, .product__media-icon, [data-gallery-prev], [data-gallery-next]'
            )
          );
        };

        target.addEventListener(
          'pointerdown',
          (event) => {
            if (event.pointerType === 'mouse' && event.button !== 0) return;
            if (isInteractiveTarget(event.target)) return;
            tracking = true;
            pointerId = event.pointerId;
            startX = event.clientX;
            startY = event.clientY;
          },
          { passive: true }
        );

        const endSwipe = (event) => {
          if (!tracking || event.pointerId !== pointerId) return;
          tracking = false;
          pointerId = null;

          const deltaX = event.clientX - startX;
          const deltaY = event.clientY - startY;
          const threshold = 45;

          if (Math.abs(deltaX) < threshold || Math.abs(deltaX) < Math.abs(deltaY)) return;

          // Mobile already supports native horizontal slider scroll; only force-cycle
          // when desktop thumbnail mode hides inactive slides (no native swipe).
          const desktopThumbnails =
            this.mql.matches && (this.dataset.desktopLayout || '').includes('thumbnail');
          if (!desktopThumbnails && event.pointerType === 'touch') return;

          this.cycleMedia(deltaX < 0 ? 1 : -1);
        };

        target.addEventListener('pointerup', endSwipe);
        target.addEventListener('pointercancel', () => {
          tracking = false;
          pointerId = null;
        });
      }

      onSlideChanged(event) {
        const current = event.detail?.currentElement;

        // Desktop thumbnail layout hides inactive slides (display:none), so slider
        // page math is unreliable — only sync is-active from scroll on mobile.
        const desktopThumbnails =
          this.mql.matches && (this.dataset.desktopLayout || '').includes('thumbnail');

        if (!desktopThumbnails && current?.dataset?.mediaId) {
          this.getSlides().forEach((element) => {
            element.classList.remove('is-active');
          });
          const activeSlide =
            current.closest?.('.product__media-item') ||
            this.elements.viewer.querySelector(`.product__media-item[data-media-id="${current.dataset.mediaId}"]`) ||
            current;
          activeSlide.classList.add('is-active');
        }

        if (!this.elements.thumbnails || !current?.dataset?.mediaId) return;

        const slideMediaId =
          current.closest?.('.product__media-item')?.dataset?.mediaId || current.dataset.mediaId;
        const thumbnail = this.elements.thumbnails.querySelector(`[data-target="${slideMediaId}"]`);
        this.setActiveThumbnail(thumbnail);
      }

      setActiveMedia(mediaId, prepend) {
        const activeMedia =
          this.elements.viewer.querySelector(`.product__media-item[data-media-id="${mediaId}"]`) ||
          this.elements.viewer.querySelector('.product__media-item[data-media-id]');
        if (!activeMedia) {
          return;
        }
        this.getSlides().forEach((element) => {
          element.classList.remove('is-active');
        });
        activeMedia.classList.add('is-active');

        if (prepend) {
          activeMedia.parentElement.firstChild !== activeMedia && activeMedia.parentElement.prepend(activeMedia);

          if (this.elements.thumbnails) {
            const activeThumbnail = this.elements.thumbnails.querySelector(`[data-target="${mediaId}"]`);
            activeThumbnail.parentElement.firstChild !== activeThumbnail && activeThumbnail.parentElement.prepend(activeThumbnail);
          }

          if (this.elements.viewer.slider) this.elements.viewer.resetPages();
        }

        this.preventStickyHeader();

        // Avoid scrolling the desktop thumbnail viewer — inactive slides are hidden,
        // and scroll events can clear the active state. Mobile still needs scroll sync.
        const desktopThumbnails =
          this.mql.matches && (this.dataset.desktopLayout || '').includes('thumbnail');

        window.setTimeout(() => {
          if (!desktopThumbnails) {
            activeMedia.parentElement.scrollTo({ left: activeMedia.offsetLeft });
          }
          const activeMediaRect = activeMedia.getBoundingClientRect();
          if (activeMediaRect.top > -0.5) return;
          const top = activeMediaRect.top + window.scrollY;
          window.scrollTo({ top: top, behavior: 'smooth' });
        });
        this.playActiveMedia(activeMedia);

        const slides = this.getSlides();
        const activeIndex = slides.findIndex((slide) => slide === activeMedia);
        if (activeIndex >= 0) this.syncGalleryDots(activeIndex + 1);

        if (!this.elements.thumbnails) return;
        const activeThumbnail = this.elements.thumbnails.querySelector(`[data-target="${mediaId}"]`);
        this.setActiveThumbnail(activeThumbnail);
        if (activeThumbnail) {
          this.announceLiveRegion(activeMedia, activeThumbnail.dataset.mediaPosition);
        }
      }

      setActiveThumbnail(thumbnail) {
        if (!this.elements.thumbnails || !thumbnail) return;

        this.elements.thumbnails
          .querySelectorAll('button')
          .forEach((element) => element.removeAttribute('aria-current'));
        const button = thumbnail.querySelector('button');
        if (button) button.setAttribute('aria-current', true);

        const desktopThumbnails =
          this.mql.matches && (this.dataset.desktopLayout || '').includes('thumbnail');

        /* Vertical left-rail: scroll the overflow column, not horizontal slider math */
        if (desktopThumbnails) {
          const rail = this.elements.thumbnails;
          if (!rail || typeof rail.getBoundingClientRect !== 'function') return;
          const railRect = rail.getBoundingClientRect();
          const thumbRect = thumbnail.getBoundingClientRect();
          const fullyVisible = thumbRect.top >= railRect.top - 1 && thumbRect.bottom <= railRect.bottom + 1;
          if (fullyVisible) return;
          const nextTop = rail.scrollTop + (thumbRect.top - railRect.top) - (rail.clientHeight / 2) + (thumbRect.height / 2);
          rail.scrollTo({ top: Math.max(0, nextTop), behavior: 'smooth' });
          return;
        }

        if (this.elements.thumbnails.isSlideVisible(thumbnail, 10)) return;
        this.elements.thumbnails.slider.scrollTo({ left: thumbnail.offsetLeft });
      }

      announceLiveRegion(activeItem, position) {
        const image = activeItem.querySelector('.product__modal-opener--image img');
        if (!image) return;
        image.onload = () => {
          this.elements.liveRegion.setAttribute('aria-hidden', false);
          this.elements.liveRegion.innerHTML = window.accessibilityStrings.imageAvailable.replace('[index]', position);
          setTimeout(() => {
            this.elements.liveRegion.setAttribute('aria-hidden', true);
          }, 2000);
        };
        image.src = image.src;
      }

      playActiveMedia(activeItem) {
        window.pauseAllMedia();
        const deferredMedia = activeItem.querySelector('.deferred-media');
        if (deferredMedia) deferredMedia.loadContent(false);
      }

      preventStickyHeader() {
        this.stickyHeader = this.stickyHeader || document.querySelector('sticky-header');
        if (!this.stickyHeader) return;
        this.stickyHeader.dispatchEvent(new Event('preventHeaderReveal'));
      }

      removeListSemantic() {
        if (!this.elements.viewer.slider) return;
        this.elements.viewer.slider.setAttribute('role', 'presentation');
        this.elements.viewer.sliderItems.forEach((slide) => slide.setAttribute('role', 'presentation'));
      }
    }
  );
}
